/*
 * Module for tracking session state, and handling core login / logout functionality
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { reactive } from "vue";
import { STATIC_CONFIG } from "./config";
import axios from "axios";
import { apiTasks } from "@/features/tasks/api";
import { Feature, User } from "@minicity/core";
export const sessionState = reactive({
    session: undefined
});
export const getAuthHeader = () => {
    var _a;
    return `Bearer ${(_a = sessionState.session) === null || _a === void 0 ? void 0 : _a.id}`;
};
// Attempts to resume any old session from info remaining in localStorage
export const tryToResumeSession = () => __awaiter(void 0, void 0, void 0, function* () {
    if (!localStorage["session"])
        throw ("No session to resume");
    const oldSession = JSON.parse(localStorage["session"]);
    const sessionId = oldSession.id;
    console.log("Attempting to resume session");
    const session = yield axios.get(`${STATIC_CONFIG.API_ROOT}/portal/caseworker/sessions/${sessionId}`)
        .then(resp => { return resp.data; }, err => { throw err; });
    storeSession(session);
    return session;
});
export const loginAsAnyUser = (email, password, mfaOneTimeCode) => __awaiter(void 0, void 0, void 0, function* () {
    const session = yield axios.post(`${STATIC_CONFIG.API_ROOT}/portal/caseworker/login`, { username: email, password: password, mfaOneTimeCode: mfaOneTimeCode }, { headers: { "Content-Type": "application/json" } }).then(resp => { return resp.data; }, err => { throw err; });
    storeSession(session);
    return session;
});
export const loginAsPartner = (email, password, mfaOneTimeCode) => __awaiter(void 0, void 0, void 0, function* () {
    const session = yield axios.post(`${STATIC_CONFIG.API_ROOT}/portal/caseworker/login/partner`, { username: email, password: password, mfaOneTimeCode: mfaOneTimeCode }, { headers: { "Content-Type": "application/json" } }).then(resp => { return resp.data; }, err => { throw err; });
    storeSession(session);
    return session;
});
export const loginAsNetworkAdmin = (email, password, mfaOneTimeCode) => __awaiter(void 0, void 0, void 0, function* () {
    const session = yield axios.post(`${STATIC_CONFIG.API_ROOT}/portal/caseworker/login/networkAdmin`, { username: email, password: password, mfaOneTimeCode: mfaOneTimeCode }, { headers: { "Content-Type": "application/json" } }).then(resp => { return resp.data; }, err => { throw err; });
    storeSession(session);
    return session;
});
export const proxyLoginAsPartnerUser = (partnerUserId) => __awaiter(void 0, void 0, void 0, function* () {
    const session = yield axios.post(`${STATIC_CONFIG.API_ROOT}/portal/caseworker/login/partner/${partnerUserId}:proxy`, null, { headers: {
            "Content-Type": "application/json",
            "Authorization": getAuthHeader()
        } }).then(resp => { return resp.data; }, err => { throw err; });
    storeSession(session);
    return session;
});
export const proxyLoginAsCitizenUser = (citizenId) => __awaiter(void 0, void 0, void 0, function* () {
    return yield axios.post(`${STATIC_CONFIG.API_ROOT}/portal/caseworker/login/citizen/${citizenId}:proxy`, null, { headers: {
            "Content-Type": "application/json",
            "Authorization": getAuthHeader()
        } }).then(resp => { return resp.data; }, err => { throw err; });
});
export const initiatePasswordReset = (email) => __awaiter(void 0, void 0, void 0, function* () {
    yield axios.post(`${STATIC_CONFIG.API_ROOT}/portal/caseworker/passwordReset`, { email: email }, { headers: { "Content-Type": "application/json" } });
});
export const validatePasswordReset = (userType, email, token) => __awaiter(void 0, void 0, void 0, function* () {
    return yield axios.post(`${STATIC_CONFIG.API_ROOT}/portal/caseworker/passwordReset/${userType}:validate`, { email: email, token: token }, { headers: { "Content-Type": "application/json" } })
        .then(() => true)
        .catch((err) => {
        if (err.response.status == 404) {
            return false; // 404 indicates invalid token
        }
        else {
            throw err;
        }
    });
});
export const applyPasswordReset = (userType, email, token, password) => __awaiter(void 0, void 0, void 0, function* () {
    yield axios.post(`${STATIC_CONFIG.API_ROOT}/portal/caseworker/passwordReset/${userType}`, { email: email, password: password, token: token }, { headers: { "Content-Type": "application/json" } });
    switch (userType) {
        case 'partner': return loginAsPartner(email, password);
        case 'networkAdmin': return loginAsNetworkAdmin(email, password);
    }
});
// Store session info in local storage
const storeSession = (session) => {
    console.log("Storing session");
    sessionState.session = session;
    localStorage["session"] = JSON.stringify(session);
    sessionState.cachedPartnerList = undefined;
    // Load tasks
    sessionState.myAssignedTasks = undefined;
    assignedTasksRefreshTimer();
};
// Poll for the current user's assigned tasks every 5 minutes
const assignedTasksRefreshTimer = () => {
    if (!sessionState.session)
        return;
    refreshMyAssignedTasks();
    setTimeout(assignedTasksRefreshTimer, 300000);
};
export const refreshMyAssignedTasks = () => {
    var _a;
    (_a = apiTasks.getMyAssignedTasks()) === null || _a === void 0 ? void 0 : _a.then(tasks => sessionState.myAssignedTasks = tasks);
};
export const endSession = () => {
    if (sessionState.session) {
        console.log("Ending session");
        axios.delete(`${STATIC_CONFIG.API_ROOT}/sessions/${sessionState.session.id}`);
    }
    clearLocalSession();
};
export const clearLocalSession = () => {
    console.log("Clearing session data");
    sessionState.session = undefined;
    delete localStorage["session"];
    sessionState.cachedPartnerList = undefined;
};
// Determines whether the specified module / feature is enabled for this user
export const isFeatureEnabled = (id) => {
    if (!sessionState.session)
        return false;
    if (sessionState.session.user.role == User.Role.NETWORK_ADMIN)
        return true;
    return sessionState.session.partnerSettings.enabledModules[id] == "ENABLED";
};
export const canSeeFeature = (id) => {
    if (!sessionState.session)
        return false;
    if (sessionState.session.user.role == User.Role.NETWORK_ADMIN)
        return true;
    switch (id) {
        case "partners":
        case "inboundMail":
            return [User.Role.NETWORK_ADMIN].includes(sessionState.session.user.role);
        case "sites":
        case "partnerUsers":
            return isFeatureEnabled(Feature.Type.SITES) && [User.Role.NETWORK_ADMIN, User.Role.PARTNER_ADMIN].includes(sessionState.session.user.role);
        case "citizens":
            return isFeatureEnabled(Feature.Type.CITIZENS);
        case "birthRecords":
            return isFeatureEnabled(Feature.Type.BIRTH_RECORDS);
        case "socialSecurity":
            return isFeatureEnabled(Feature.Type.SOCIAL_SECURITY);
        case "georgiaBenefits":
            return isFeatureEnabled(Feature.Type.GEORGIA_DFCS_BENEFITS);
        case "analytics":
            return isFeatureEnabled(Feature.Type.ANALYTICS) && [User.Role.NETWORK_ADMIN, User.Role.PARTNER_ADMIN].includes(sessionState.session.user.role);
        case "tasks":
            return isFeatureEnabled(Feature.Type.TASKS);
        case "lift_2_0":
            return isFeatureEnabled(Feature.Type.LIFT_2_0);
        case "shipping_label_printing":
            return isFeatureEnabled(Feature.Type.SHIPPING_LABEL_PRINTING) && (isNetworkAdmin() || !STATIC_CONFIG.SERVICE_ONLY_MODE);
        default:
            return false;
    }
};
export const isPartnerAdmin = () => {
    if (!sessionState.session)
        return false;
    return sessionState.session.user.role == User.Role.PARTNER_ADMIN;
};
export const isNetworkAdmin = () => {
    if (!sessionState.session)
        return false;
    return sessionState.session.user.role == User.Role.NETWORK_ADMIN;
};
